import { Alert, Button, Card, Table } from "flowbite-react";
import React, { useEffect, useRef, useState } from "react";
import { FaPrint } from "react-icons/fa";
import {
  formatDocument,
  formatToReal,
  isValidCNPJ,
  isValidCPF,
} from "../../utils/utils";
import axios from "../../config/axiosInstance";
import { QUERIES } from "../../config/apiConfig";
import { HiClipboardList, HiInformationCircle } from "react-icons/hi";
import { HiBuildingLibrary, HiCalendarDays } from "react-icons/hi2";
import HeaderQuery from "./HeaderQuery";
import usePDF from "../../hooks/usePDF";
import TitleStripe from "../common/TitleStripe";
import CardQueryDescription from "../common/CardQueryDescription";
import QueryInput from "../inputs/QueryInput";
import SliderToPreview from "../common/SliderToPreview";
import QueryLoading from "../common/QueryLoading";
import NoOcurrence from "../common/query/NoOcurrence";
import FooterQuery from "./FooterQuery";
import { useReactToPrint } from "react-to-print";

function HighlightStat({ icon = "HiClipboardList", label, value, hint }) {
  const icons = {
    HiClipboardList: HiClipboardList,
    HiBuildingLibrary: HiBuildingLibrary,
    HiCalendarDays: HiCalendarDays,
  };
  const IconCmp = icons[icon] || HiClipboardList;

  return (
    <div
      className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
      onMouseMove={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        event.currentTarget.style.setProperty(
          "--x",
          `${event.clientX - rect.left}px`
        );
        event.currentTarget.style.setProperty(
          "--y",
          `${event.clientY - rect.top}px`
        );
      }}
    >
      <div
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(600px circle at var(--x,50%) var(--y,50%), rgba(59,130,246,0.08), transparent 40%)",
        }}
      />
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
          <IconCmp className="h-6 w-6 text-blue-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wide text-gray-500">
            {label}
          </div>
          <div className="text-xl font-semibold leading-tight text-gray-900">
            {value ?? "-"}
          </div>
        </div>
      </div>
      <p className="mt-2 text-[12px] leading-5 text-gray-500">{hint}</p>
    </div>
  );
}

const getScoreStyle = (score) => {
  if (score <= 300)
    return { bg: "bg-red-100", text: "text-red-700", border: "border-red-400" };
  if (score <= 600)
    return {
      bg: "bg-yellow-100",
      text: "text-yellow-700",
      border: "border-yellow-400",
    };
  return {
    bg: "bg-green-100",
    text: "text-green-700",
    border: "border-green-400",
  };
};

const InfoItem = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2">
    <div className="min-w-0">
      <span className="block text-[11px] font-medium uppercase tracking-wide text-gray-500">
        {label}
      </span>
      <span className="block text-sm font-semibold text-gray-900">
        {value || "-"}
      </span>
    </div>
  </div>
);

const ScrBacenScoreQuery = ({
  query,
  selectedPrice,
  dataFromLocalStorage = null,
  hasHeaderPDF = "true",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [document, setDocument] = useState("");
  const [error, setError] = useState("");
  const [errorData, setErrorData] = useState("");
  const [data, setData] = useState(null);
  const componentRef = useRef();
  const reactToPrintFn = useReactToPrint({ contentRef: componentRef });
  const relatorioSCR = data?.CREDCADASTRAL?.RELATORIO_SCR;
  const consolidadoSCR = relatorioSCR?.CONSOLIDADO;
  const operacoesSCR = relatorioSCR?.OPERACOES;

  useEffect(() => {
    if (dataFromLocalStorage) {
      setData(dataFromLocalStorage);
      setDocument(dataFromLocalStorage?.resumoRetorno?.document);
    }
  }, []);

  const fetchInfo = async (type) => {
    return await axios(
      `${QUERIES.GET_V2_QUERY}/scr-bacen-score/${info.replace(
        /\D/g,
        ""
      )}?type=${type}`
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setDocument(info);

    const unmaskedValue = info.replace(/\D/g, "");
    let isValid = true;
    let type = "F";

    if (unmaskedValue.length <= 11) {
      if (!isValidCPF(unmaskedValue)) {
        setError("CPF inválido!");
        isValid = false;
      }
    } else {
      if (!isValidCNPJ(unmaskedValue)) {
        setError("CNPJ inválido!");
        isValid = false;
      }
      type = "J";
    }

    if (isValid) {
      try {
        setData(null);
        setIsLoading(true);
        setErrorData("");
        const { data: dados } = await fetchInfo(type);

        if (Object.keys(dados.data.CREDCADASTRAL).length === 0) {
          throw new Error("Algum erro ocorreu com sua consulta");
        }
        setData(dados.data);
      } catch (error) {
        setErrorData(
          typeof error?.response?.data?.message !== "object"
            ? error?.response?.data?.message
            : "Algum erro ocorreu com sua consulta"
        );
      } finally {
        setIsLoading(false);
      }
    } else {
      setData(null);
    }
  };

  return (
    <>
      {!dataFromLocalStorage && (
        <>
          <CardQueryDescription
            title={query?.name || "Informações da consulta"}
            infos={[
              "Dados Cadastrais",
              "SCR Score",
              "Créditos a vencer",
              "Créditos vencidos",
              "Limite de crédito",
              "Prejuízos",
              "Operações",
            ]}
            price={selectedPrice}
          >
            <form
              className="flex flex-col gap-2 bg-white p-2 rounded-lg shadow-md w-fit"
              onSubmit={handleSubmit}
            >
              <div className="flex flex-col md:flex-row gap-2 items-center">
                <QueryInput
                  info={info}
                  setInfo={setInfo}
                  isLoading={isLoading}
                  error={error}
                  type="CPF/CNPJ"
                />
                {!errorData && data && (
                  <Button
                    className="bg-gray-600"
                    onClick={() => reactToPrintFn()}
                  >
                    <div className="flex gap-2 items-center">
                      <FaPrint />
                      <div>Imprimir</div>
                    </div>
                  </Button>
                )}
              </div>
            </form>
          </CardQueryDescription>
        </>
      )}

      <SliderToPreview />

      {errorData && (
        <Alert color="failure" icon={HiInformationCircle}>
          <span className="font-medium">{errorData}</span>
        </Alert>
      )}
      {!errorData && (
        <div
          className={`overflow-x-scroll print-content mobile-fit ${
            data || isLoading ? "block" : "hidden"
          }`}
          ref={componentRef}
        >
          {!isLoading ? (
            <Card className="relative overflow-hidden p-5 card-pdf">
              <HeaderQuery hasHeader={hasHeaderPDF} title={query?.name} />
              <div className="space-y-10" id="credito-vencido-bacen">
                <section>
                  <TitleStripe>Dados BACEN</TitleStripe>

                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                    <HighlightStat
                      icon="HiClipboardList"
                      label="Quantidade de Operações"
                      value={relatorioSCR?.QUANTIDADE_OPERACOES ?? "-"}
                      hint="Total de contratos/linhas de crédito reportados ao SCR no período-base. Pode incluir operações ativas, baixadas e com diferentes modalidades."
                    />

                    <HighlightStat
                      icon="HiBuildingLibrary"
                      label="Quantidade de Instituições"
                      value={relatorioSCR?.QUANTIDADE_INSTITUICOES ?? "-"}
                      hint="Número de instituições financeiras que reportaram operações para este CPF/CNPJ. Indica diversidade de relacionamento bancário."
                    />

                    <HighlightStat
                      icon="HiCalendarDays"
                      label="Início do Relacionamento"
                      value={relatorioSCR?.DATA_INICIO_RELACIONAMENTO ?? "-"}
                      hint="Primeira ocorrência de relacionamento encontrada no histórico do SCR. Não é a data de abertura do CPF/CNPJ, e pode variar conforme o reporte."
                    />
                  </div>

                  <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500/80 via-sky-400/80 to-emerald-400/80" />
                      <div className="mb-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                            <HiClipboardList className="h-4 w-4 text-blue-600" />
                          </div>
                          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                            Resumo Geral
                          </h3>
                        </div>
                        <span className="rounded-full bg-blue-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-blue-600">
                          SCR
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-3 text-sm">
                        <InfoItem
                          label="Documento"
                          value={formatDocument(
                            document || relatorioSCR?.DOCUMENTO
                          )}
                        />
                        <InfoItem
                          label="Tipo de Documento"
                          value={relatorioSCR?.TIPO_DOCUMENTO}
                        />
                        <InfoItem
                          label="Coobrigação Assumida"
                          value={formatToReal(relatorioSCR?.COOBRIGACAO_ASSUMIDA)}
                        />
                        <InfoItem
                          label="Coobrigação Recebida"
                          value={formatToReal(relatorioSCR?.COOBRIGACAO_RECEBIDA)}
                        />
                        <InfoItem
                          label="Data Base Consultada"
                          value={relatorioSCR?.DATABASE_CONSULTADA}
                        />
                      </div>
                    </div>

                    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500/80 via-teal-400/80 to-cyan-400/80" />
                      <div className="mb-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50">
                            <HiBuildingLibrary className="h-4 w-4 text-emerald-600" />
                          </div>
                          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                            Indicadores de Confiabilidade
                          </h3>
                        </div>
                        <span className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-emerald-600">
                          Qualidade
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-3 text-sm">
                        <InfoItem
                          label="Percentual de Documentos Processados"
                          value={
                            relatorioSCR?.PERCENTUAL_DOCUMENTOS_PROCESSADOS !=
                            null
                              ? `${relatorioSCR?.PERCENTUAL_DOCUMENTOS_PROCESSADOS}%`
                              : "-"
                          }
                        />
                        <InfoItem
                          label="Percentual de Volume Processado"
                          value={
                            relatorioSCR?.PERCENTUAL_VOLUME_PROCESSADO != null
                              ? `${relatorioSCR?.PERCENTUAL_VOLUME_PROCESSADO}%`
                              : "-"
                          }
                        />
                        <InfoItem
                          label="Operações em Discordância"
                          value={relatorioSCR?.QUANTIDADE_OPERACOES_DISCORDANCIA}
                        />
                        <InfoItem
                          label="Total Discordância"
                          value={formatToReal(
                            relatorioSCR?.RESPONSABILIDADE_TOTAL_DISCORDANCIA
                          )}
                        />
                        <InfoItem
                          label="Operações Subjudice"
                          value={relatorioSCR?.QUANTIDADE_OPERACOES_SUBJUDICE}
                        />
                        <InfoItem
                          label="Total Subjudice"
                          value={formatToReal(
                            relatorioSCR?.RESPONSABILIDADE_TOTAL_SUBJUDICE
                          )}
                        />
                        <InfoItem
                          label="Risco Indireto (Vendor)"
                          value={formatToReal(relatorioSCR?.RISCO_INDIRETO_VENDOR)}
                        />
                      </div>
                    </div>
                  </div>
                </section>

                <section id="prejuizo-bacen">
                  <TitleStripe className="mb-4">SCR Score</TitleStripe>
                  {relatorioSCR?.SCORE?.PONTUACAO != null ? (
                    (() => {
                      const scoreNumber = Number(relatorioSCR?.SCORE?.PONTUACAO);
                      const safeScore = Number.isFinite(scoreNumber)
                        ? scoreNumber
                        : null;
                      const { bg, text, border } = getScoreStyle(
                        safeScore ?? 0
                      );
                      return (
                        <div className="flex flex-col items-center gap-6 rounded-xl border border-gray-200 bg-white p-6 lg:flex-row">
                          <div className="relative">
                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400/40 via-transparent to-emerald-300/40 blur-md" />
                            <div className="absolute inset-1 rounded-full bg-white/70" />
                            <div
                              className={`relative flex h-32 w-32 items-center justify-center rounded-full border-4 shadow-inner ${bg} ${border} ${text}`}
                            >
                              <span className="text-3xl font-extrabold tracking-tight">
                                {safeScore ?? "-"}
                              </span>
                            </div>
                          </div>

                          <div className="flex-1 text-sm leading-relaxed text-gray-700">
                            <p className="mb-1">
                              <span className="font-semibold text-gray-800">
                                Importante:
                              </span>{" "}
                              a decisão de concessão de crédito é responsabilidade
                              exclusiva do concedente.
                            </p>
                            <p>
                              Os dados apresentados têm caráter informativo e{" "}
                              <strong>
                                não devem ser utilizados como justificativa única
                              </strong>{" "}
                              para aprovação ou recusa.
                            </p>
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="mt-3 text-sm font-semibold text-red-600">
                      ESTE DOCUMENTO NÃO TEM TRANSAÇÕES NO PERÍODO DA PESQUISA.
                    </div>
                  )}
                </section>

                {renderConsolidado(
                  "Crédito a Vencer",
                  consolidadoSCR?.CREDITO_AVENCER
                )}
                {renderConsolidado(
                  "Crédito Vencido",
                  consolidadoSCR?.CREDITO_VENCIDO,
                  true
                )}
                {renderConsolidado(
                  "Limite de Crédito",
                  consolidadoSCR?.LIMITE_CREDITO
                )}
                {renderConsolidado(
                  "Prejuízos",
                  consolidadoSCR?.PREJUIZO,
                  true
                )}

                <section>
                  <TitleStripe>Operações</TitleStripe>
                  {operacoesSCR?.length ? (
                    <div className="mt-3 space-y-6">
                      <div className="overflow-x-auto rounded-xl border border-gray-200">
                        <Table striped>
                          <Table.Head>
                            <Table.HeadCell>Modalidade</Table.HeadCell>
                            <Table.HeadCell className="text-center">
                              Total
                            </Table.HeadCell>
                            <Table.HeadCell className="text-center">
                              Percentual
                            </Table.HeadCell>
                            <Table.HeadCell className="text-center">
                              Variação cambial
                            </Table.HeadCell>
                          </Table.Head>
                          <Table.Body>
                            {operacoesSCR.map((operation, idx) => (
                              <Table.Row
                                key={`operacao-${operation?.MODALIDADE?.DESCRICAO || "scr"}-${idx}`}
                              >
                                <Table.Cell>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {operation?.MODALIDADE?.DESCRICAO || "-"}
                                  </div>
                                  <div className="text-[11px] text-gray-500">
                                    {operation?.SUB_MODALIDADE?.DESCRICAO || "-"}
                                  </div>
                                </Table.Cell>
                                <Table.Cell className="text-center">
                                  {operation?.TOTAL != null
                                    ? formatToReal(operation?.TOTAL)
                                    : "-"}
                                </Table.Cell>
                                <Table.Cell className="text-center">
                                  {operation?.PERCENTUAL != null
                                    ? `${operation?.PERCENTUAL}%`
                                    : "-"}
                                </Table.Cell>
                                <Table.Cell className="px-4 text-center">
                                  {operation?.VARIACAO_CAMBIAL ?? "-"}
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table>
                      </div>

                      <div className="space-y-5">
                        <TitleStripe>Operações detalhadas</TitleStripe>
                        {operacoesSCR.map((operation, idx) => (
                          <div
                            key={`operacao-detail-${operation?.MODALIDADE?.DESCRICAO || "scr"}-${idx}`}
                            className="mt-4"
                          >
                            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
                              <div>
                                <div className="text-sm font-semibold text-gray-900">
                                  {operation?.MODALIDADE?.DESCRICAO || "-"}
                                </div>
                                <p className="text-[11px] text-gray-500">
                                  {operation?.SUB_MODALIDADE?.DESCRICAO || "-"}
                                </p>
                              </div>
                              <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500 md:text-right">
                                <div>
                                  <span className="font-semibold text-gray-900">
                                    Total:
                                  </span>{" "}
                                  {operation?.TOTAL != null
                                    ? formatToReal(operation?.TOTAL)
                                    : "-"}
                                </div>
                                <div>
                                  <span className="font-semibold text-gray-900">
                                    Percentual:
                                  </span>{" "}
                                  {operation?.PERCENTUAL != null
                                    ? `${operation?.PERCENTUAL}%`
                                    : "-"}
                                </div>
                                <div>
                                  <span className="font-semibold text-gray-900">
                                    Variação cambial:
                                  </span>{" "}
                                  {operation?.VARIACAO_CAMBIAL ?? "-"}
                                </div>
                              </div>
                            </div>

                            <div className="mt-4 overflow-x-auto">
                              <Table striped>
                                <Table.Head>
                                  <Table.HeadCell>Código</Table.HeadCell>
                                  <Table.HeadCell>Descrição</Table.HeadCell>
                                  <Table.HeadCell className="text-center">
                                    Valor
                                  </Table.HeadCell>
                                  <Table.HeadCell className="text-center">
                                    Meses
                                  </Table.HeadCell>
                                  <Table.HeadCell className="text-center">
                                    Percentual
                                  </Table.HeadCell>
                                  <Table.HeadCell className="text-center">
                                    Restritivo
                                  </Table.HeadCell>
                                </Table.Head>
                                <Table.Body>
                                  {operation?.VENCIMENTOS?.map((item, itemIdx) => (
                                    <Table.Row
                                      key={`${operation?.MODALIDADE?.DESCRICAO || "scr"}-${item?.CODIGO}-${itemIdx}`}
                                    >
                                      <Table.Cell>{item?.CODIGO || "-"}</Table.Cell>
                                      <Table.Cell className="max-w-[20ch]">
                                        <span className="text-[12px] text-gray-500">
                                          {item?.DESCRICAO || "-"}
                                        </span>
                                      </Table.Cell>
                                      <Table.Cell className="text-center">
                                        {item?.VALOR != null
                                          ? formatToReal(item?.VALOR)
                                          : "-"}
                                      </Table.Cell>
                                      <Table.Cell className="text-center">
                                        {item?.QTD_MESES ?? "-"}
                                      </Table.Cell>
                                      <Table.Cell className="text-center">
                                        {item?.PERCENTUAL != null
                                          ? `${item?.PERCENTUAL}%`
                                          : "-"}
                                      </Table.Cell>
                                      <Table.Cell className="text-center">
                                        <span
                                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                            item?.RESTRITIVO === "1"
                                              ? "bg-rose-100 text-rose-700"
                                              : "bg-emerald-100 text-emerald-700"
                                          }`}
                                        >
                                          {item?.RESTRITIVO === "1" ? "Sim" : "Não"}
                                        </span>
                                      </Table.Cell>
                                    </Table.Row>
                                  ))}
                                  {!operation?.VENCIMENTOS?.length && (
                                    <Table.Row>
                                      <Table.Cell colSpan={6} className="text-center">
                                        Nenhum vencimento encontrado.
                                      </Table.Cell>
                                    </Table.Row>
                                  )}
                                </Table.Body>
                              </Table>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <NoOcurrence />
                  )}
                </section>
              </div>

              <FooterQuery hasHeader={hasHeaderPDF} />
            </Card>
          ) : (
            <QueryLoading />
          )}
        </div>
      )}
    </>
  );
};

const renderConsolidado = (title, bloco, highlight = false) => {
  const totalPercentual = bloco?.PERCENTUAL ?? bloco?.percentual;

  return (
    <section>
      <TitleStripe>{title}</TitleStripe>
      {bloco?.OPERACOES?.length > 0 ? (
        <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200">
          <Table striped>
            <Table.Head>
              <Table.HeadCell>Descrição</Table.HeadCell>
              <Table.HeadCell className="text-center">Valor</Table.HeadCell>
              <Table.HeadCell className="text-center">Qtd. Meses</Table.HeadCell>
              <Table.HeadCell className="text-center">Percentual</Table.HeadCell>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Cell className="font-bold">Total</Table.Cell>
                <Table.Cell
                  className={`text-center font-bold ${
                    highlight ? "text-red-600" : ""
                  }`}
                >
                  {formatToReal(bloco?.VALOR)}
                </Table.Cell>
                <Table.Cell className="text-center font-bold">-</Table.Cell>
                <Table.Cell className="text-center font-bold">
                  {totalPercentual != null ? `${totalPercentual}%` : "-"}
                </Table.Cell>
              </Table.Row>
              {bloco?.OPERACOES?.map((item, index) => (
                <Table.Row key={`${title}-${index}`}>
                  <Table.Cell>{item?.DESCRICAO || "-"}</Table.Cell>
                  <Table.Cell className="text-center">
                    {item?.VALOR != null ? formatToReal(item?.VALOR) : "-"}
                  </Table.Cell>
                  <Table.Cell className="text-center">
                    {item?.QTD_MESES ?? "-"}
                  </Table.Cell>
                  <Table.Cell className="text-center">
                    {item?.PERCENTUAL != null
                      ? `${item?.PERCENTUAL}%`
                      : "-"}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      ) : (
        <NoOcurrence />
      )}
    </section>
  );
};

export default ScrBacenScoreQuery;
